<script lang="ts" setup>
import { ref, watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';

/**
 * 自由入力の複数値（タグ）入力。
 * 値はカンマ結合の単一文字列として `modelValue` を入出力する。
 * カンマは区切り文字として扱うため、各タグにカンマは含められない。
 */
const props = withDefaults(
  defineProps<{
    /** カンマ結合されたタグ文字列 */
    modelValue: string;
    label: string;
    placeholder?: string;
    disabled?: boolean;
  }>(),
  { placeholder: '', disabled: false }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'editing'): void;
}>();

/**
 * カンマ結合文字列をタグ配列へ変換する。
 * @param value - カンマ結合文字列
 * @returns 空要素を除いたタグ配列
 */
const parseTags = (value: string): string[] =>
  value
    ? value
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    : [];

const tags = ref<string[]>(parseTags(props.modelValue));
const inputText = ref<string>('');

watch(
  () => props.modelValue,
  (value) => {
    tags.value = parseTags(value);
  }
);

/** 現在のタグ配列をカンマ結合文字列として親へ通知する */
const emitValue = () => {
  emit('update:modelValue', tags.value.join(','));
};

/**
 * 入力中テキストをタグとして確定する。
 * カンマ区切りの貼り付けにも対応し、重複・空文字は追加しない。
 */
const commitInput = () => {
  const candidates = parseTags(inputText.value);
  if (candidates.length === 0) {
    inputText.value = '';
    return;
  }
  let didAdd = false;
  candidates.forEach((candidate) => {
    if (!tags.value.includes(candidate)) {
      tags.value.push(candidate);
      didAdd = true;
    }
  });
  inputText.value = '';
  if (didAdd) emitValue();
};

/**
 * 指定indexのタグを削除する。
 * @param index - 削除するタグのindex
 */
const removeTag = (index: number) => {
  tags.value.splice(index, 1);
  emitValue();
};

/**
 * キー入力を処理する。Enterまたはカンマでタグを確定する。
 * @param event - キーボードイベント
 */
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault();
    commitInput();
  } else if (event.key === 'Backspace' && inputText.value === '' && tags.value.length > 0) {
    removeTag(tags.value.length - 1);
  }
};

/**
 * Input時にemit流す処理
 */
const onInput = () => {
  emit('editing');
};
</script>
<template>
  <div>
    <div class="multi-tag-input__label">
      <label v-if="label !== ''" class="multi-tag-input__label-text">{{ label }}</label>
    </div>
    <div class="multi-tag-input" :class="{ 'multi-tag-input--disabled': disabled }">
      <div v-for="(tag, index) in tags" :key="index + tag" class="multi-tag-input__chip">
        <span class="multi-tag-input__chip-text">{{ tag }}</span>
        <button v-if="!disabled" type="button" class="multi-tag-input__chip-close" @click="removeTag(index)">
          <SvgLoader name="close" />
        </button>
      </div>
      <input
        v-model="inputText"
        class="multi-tag-input__field"
        type="text"
        :placeholder="tags.length === 0 ? placeholder : ''"
        :disabled="disabled"
        @input="onInput"
        @keydown="onKeydown"
        @blur="commitInput"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.multi-tag-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;

  min-height: 38px;
  padding: 5px 8px;
  box-sizing: border-box;
  width: 100%;

  background-color: $semanticWhite;
  border: 1px solid transparent;

  &--disabled {
    background-color: $surfaceSecondary;
  }

  &__chip {
    display: grid;
    grid-template-columns: auto 16px;
    align-items: center;
    gap: 4px;

    min-height: 24px;
    padding: 4px;

    border: 1px solid $borderSecondary;
    border-radius: 4px;
    background-color: white;

    color: $textSecondary;
    font-size: 12px;
    font-weight: 700;
    line-height: 12px;

    &-text {
      overflow-wrap: anywhere;
    }

    &-close {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      background: transparent;
      cursor: pointer;
    }
  }

  &__field {
    flex: 1 1 60px;
    min-width: 60px;
    height: 26px;

    border: none;
    outline: none;
    background-color: transparent;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.27px;
    color: $textDefault;

    &::placeholder {
      color: $textSecondary;
    }

    &:disabled {
      background-color: transparent;
    }
  }

  &__label {
    display: grid;
    grid-template-columns: auto auto;
    gap: 3px;
    justify-content: space-between;
    align-items: center;

    &-text {
      padding: 8px 0;
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 18.2px;
      text-align: center;
      color: $textDefault;
    }
  }
}
</style>
